<template>
    <v-dialog v-model="visible" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card class="booking-card">
            <!-- Header -->
            <div class="card-header">
                <div class="left-section">
                    <div class="header-badge">{{ $t('bookingAnalytics.header.badge') }}</div>
                    <h2>{{ $t('bookingAnalytics.header.title') }}</h2>
                    <p class="header-description">
                        {{ $t('bookingAnalytics.header.description') }}
                    </p>
                </div>
                <div class="right-section">
                    <button @click="closeDialog" class="dialog-close-btn">
                        <v-icon>mdi-close</v-icon>
                    </button>
                </div>
            </div>

            <!-- Main Content -->
            <div class="booking-container">
                <!-- Overview Stats -->
                <div class="stats-overview">
                    <div class="stat-card">
                        <div class="stat-icon active">
                            <v-icon size="32" color="white">mdi-calendar-check</v-icon>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">{{ totalBookings }}</span>
                            <span class="stat-label">{{ $t('bookingAnalytics.stats.totalBookings') }}</span>
                            <span class="stat-trend positive">
                                <v-icon size="14">mdi-trending-up</v-icon>
                                {{ $t('bookingAnalytics.stats.trends.vsLastWeek') }}
                            </span>
                        </div>
                    </div>

                    <div class="stat-card">
                        <div class="stat-icon occupied">
                            <v-icon size="32" color="white">mdi-desk</v-icon>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">{{ occupancyRate }}%</span>
                            <span class="stat-label">{{ $t('bookingAnalytics.stats.occupancyRate') }}</span>
                            <span class="stat-trend positive">
                                <v-icon size="14">mdi-trending-up</v-icon>
                                {{ $t('bookingAnalytics.stats.trends.vsAverage') }}
                            </span>
                        </div>
                    </div>

                    <div class="stat-card">
                        <div class="stat-icon available">
                            <v-icon size="32" color="white">mdi-office-building</v-icon>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">{{ availableSpaces }}</span>
                            <span class="stat-label">{{ $t('bookingAnalytics.stats.availableSpaces') }}</span>
                            <span class="stat-trend neutral">
                                <v-icon size="14">mdi-minus</v-icon>
                                {{ $t('bookingAnalytics.stats.trends.realTime') }}
                            </span>
                        </div>
                    </div>

                    <div class="stat-card">
                        <div class="stat-icon peak">
                            <v-icon size="32" color="white">mdi-clock-time-four</v-icon>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">{{ peakHours }}</span>
                            <span class="stat-label">{{ $t('bookingAnalytics.stats.peakHours') }}</span>
                            <span class="stat-trend neutral">
                                <v-icon size="14">mdi-information-outline</v-icon>
                                {{ $t('bookingAnalytics.stats.trends.peakTime') }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Key Features -->
                <div class="features-section">
                    <div class="section-header">
                        <h3>{{ $t('bookingAnalytics.features.title') }}</h3>
                        <p>{{ $t('bookingAnalytics.features.subtitle') }}</p>
                    </div>
                    
                    <v-row class="features-grid">
                        <v-col cols="12" md="6" lg="3" class="pa-3">
                            <div class="feature-card instant">
                                <div class="feature-icon">
                                    <v-icon size="28" color="white">mdi-lightning-bolt</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.features.instantBooking.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.features.instantBooking.description') }}</p>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="3" class="pa-3">
                            <div class="feature-card calendar">
                                <div class="feature-icon">
                                    <v-icon size="28" color="white">mdi-calendar-sync</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.features.calendarIntegration.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.features.calendarIntegration.description') }}</p>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="3" class="pa-3">
                            <div class="feature-card recurring">
                                <div class="feature-icon">
                                    <v-icon size="28" color="white">mdi-repeat</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.features.recurringBookings.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.features.recurringBookings.description') }}</p>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="3" class="pa-3">
                            <div class="feature-card notification">
                                <div class="feature-icon">
                                    <v-icon size="28" color="white">mdi-bell-ring</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.features.smartNotifications.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.features.smartNotifications.description') }}</p>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="3" class="pa-3">
                            <div class="feature-card analytics">
                                <div class="feature-icon">
                                    <v-icon size="28" color="white">mdi-chart-areaspline</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.features.usageAnalytics.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.features.usageAnalytics.description') }}</p>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="3" class="pa-3">
                            <div class="feature-card mobile">
                                <div class="feature-icon">
                                    <v-icon size="28" color="white">mdi-cellphone</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.features.mobileFirst.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.features.mobileFirst.description') }}</p>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="3" class="pa-3">
                            <div class="feature-card checkin">
                                <div class="feature-icon">
                                    <v-icon size="28" color="white">mdi-qrcode-scan</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.features.qrCheckin.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.features.qrCheckin.description') }}</p>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="3" class="pa-3">
                            <div class="feature-card automation">
                                <div class="feature-icon">
                                    <v-icon size="28" color="white">mdi-robot-outline</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.features.autoRelease.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.features.autoRelease.description') }}</p>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Charts Section -->
                <div class="charts-section">
                    <v-row>
                        <!-- Space Distribution -->
                        <v-col cols="12" lg="6" class="pa-3">
                            <div class="chart-container">
                                <div class="chart-header">
                                    <h4>{{ $t('bookingAnalytics.charts.spaceDistribution.title') }}</h4>
                                    <p>{{ $t('bookingAnalytics.charts.spaceDistribution.subtitle') }}</p>
                                </div>
                                <div class="chart-wrapper">
                                    <apexchart 
                                        type="donut" 
                                        height="320" 
                                        :options="donutOptions" 
                                        :series="donutSeries" 
                                    />
                                </div>
                                <div class="chart-legend">
                                    <div class="legend-item">
                                        <span class="legend-color" style="background: #1E5A8E;"></span>
                                        <span>{{ $t('bookingAnalytics.charts.spaceDistribution.meetingRooms') }}</span>
                                        <span class="legend-value">44%</span>
                                    </div>
                                    <div class="legend-item">
                                        <span class="legend-color" style="background: #FF6B35;"></span>
                                        <span>{{ $t('bookingAnalytics.charts.spaceDistribution.teamZones') }}</span>
                                        <span class="legend-value">25%</span>
                                    </div>
                                    <div class="legend-item">
                                        <span class="legend-color" style="background: #2B7BC4;"></span>
                                        <span>{{ $t('bookingAnalytics.charts.spaceDistribution.privateDesks') }}</span>
                                        <span class="legend-value">18%</span>
                                    </div>
                                    <div class="legend-item">
                                        <span class="legend-color" style="background: #FFA07A;"></span>
                                        <span>{{ $t('bookingAnalytics.charts.spaceDistribution.otherSpaces') }}</span>
                                        <span class="legend-value">13%</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Weekly Booking Trends -->
                        <v-col cols="12" lg="6" class="pa-3">
                            <div class="chart-container">
                                <div class="chart-header">
                                    <h4>{{ $t('bookingAnalytics.charts.weeklyTrends.title') }}</h4>
                                    <p>{{ $t('bookingAnalytics.charts.weeklyTrends.subtitle') }}</p>
                                </div>
                                <div class="chart-wrapper">
                                    <apexchart 
                                        type="bar" 
                                        height="320" 
                                        :options="barOptions" 
                                        :series="barSeries" 
                                    />
                                </div>
                            </div>
                        </v-col>

                        <!-- Peak Usage Hours -->
                        <v-col cols="12" lg="6" class="pa-3">
                            <div class="chart-container">
                                <div class="chart-header">
                                    <h4>{{ $t('bookingAnalytics.charts.peakHours.title') }}</h4>
                                    <p>{{ $t('bookingAnalytics.charts.peakHours.subtitle') }}</p>
                                </div>
                                <div class="chart-wrapper">
                                    <apexchart 
                                        type="area" 
                                        height="320" 
                                        :options="areaOptions" 
                                        :series="areaSeries" 
                                    />
                                </div>
                            </div>
                        </v-col>

                        <!-- Most Popular Spaces -->
                        <v-col cols="12" lg="6" class="pa-3">
                            <div class="chart-container">
                                <div class="chart-header">
                                    <h4>{{ $t('bookingAnalytics.charts.popularSpaces.title') }}</h4>
                                    <p>{{ $t('bookingAnalytics.charts.popularSpaces.subtitle') }}</p>
                                </div>
                                <div class="chart-wrapper">
                                    <apexchart 
                                        type="bar" 
                                        height="320" 
                                        :options="horizontalBarOptions" 
                                        :series="horizontalBarSeries" 
                                    />
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Space Types Section -->
                <div class="space-types-section">
                    <div class="section-header">
                        <h3>{{ $t('bookingAnalytics.spaceTypes.title') }}</h3>
                        <p>{{ $t('bookingAnalytics.spaceTypes.subtitle') }}</p>
                    </div>

                    <v-row class="space-types-grid">
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="space-type-card meeting">
                                <div class="space-icon">
                                    <v-icon size="36" color="white">mdi-account-group</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.spaceTypes.meetingRoom.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.spaceTypes.meetingRoom.description') }}</p>
                                <div class="space-specs">
                                    <div class="spec">
                                        <v-icon size="16">mdi-account-multiple</v-icon>
                                        <span>{{ $t('bookingAnalytics.spaceTypes.meetingRoom.capacity') }}</span>
                                    </div>
                                    <div class="spec">
                                        <v-icon size="16">mdi-monitor</v-icon>
                                        <span>{{ $t('bookingAnalytics.spaceTypes.meetingRoom.equipment') }}</span>
                                    </div>
                                </div>
                                <div class="space-availability">
                                    <span class="available-now">{{ $t('bookingAnalytics.spaceTypes.meetingRoom.availability') }}</span>
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="space-type-card desk">
                                <div class="space-icon">
                                    <v-icon size="36" color="white">mdi-desk</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.spaceTypes.privateDesk.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.spaceTypes.privateDesk.description') }}</p>
                                <div class="space-specs">
                                    <div class="spec">
                                        <v-icon size="16">mdi-account</v-icon>
                                        <span>{{ $t('bookingAnalytics.spaceTypes.privateDesk.capacity') }}</span>
                                    </div>
                                    <div class="spec">
                                        <v-icon size="16">mdi-monitor</v-icon>
                                        <span>{{ $t('bookingAnalytics.spaceTypes.privateDesk.equipment') }}</span>
                                    </div>
                                </div>
                                <div class="space-availability">
                                    <span class="available-now">{{ $t('bookingAnalytics.spaceTypes.privateDesk.availability') }}</span>
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="space-type-card team">
                                <div class="space-icon">
                                    <v-icon size="36" color="white">mdi-account-group-outline</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.spaceTypes.teamZone.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.spaceTypes.teamZone.description') }}</p>
                                <div class="space-specs">
                                    <div class="spec">
                                        <v-icon size="16">mdi-account-multiple</v-icon>
                                        <span>{{ $t('bookingAnalytics.spaceTypes.teamZone.capacity') }}</span>
                                    </div>
                                    <div class="spec">
                                        <v-icon size="16">mdi-table-large</v-icon>
                                        <span>{{ $t('bookingAnalytics.spaceTypes.teamZone.equipment') }}</span>
                                    </div>
                                </div>
                                <div class="space-availability">
                                    <span class="available-now">{{ $t('bookingAnalytics.spaceTypes.teamZone.availability') }}</span>
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="space-type-card focus">
                                <div class="space-icon">
                                    <v-icon size="36" color="white">mdi-eye-off</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.spaceTypes.focusRoom.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.spaceTypes.focusRoom.description') }}</p>
                                <div class="space-specs">
                                    <div class="spec">
                                        <v-icon size="16">mdi-account</v-icon>
                                        <span>{{ $t('bookingAnalytics.spaceTypes.focusRoom.capacity') }}</span>
                                    </div>
                                    <div class="spec">
                                        <v-icon size="16">mdi-volume-off</v-icon>
                                        <span>{{ $t('bookingAnalytics.spaceTypes.focusRoom.equipment') }}</span>
                                    </div>
                                </div>
                                <div class="space-availability">
                                    <span class="available-now">{{ $t('bookingAnalytics.spaceTypes.focusRoom.availability') }}</span>
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="space-type-card phone">
                                <div class="space-icon">
                                    <v-icon size="36" color="white">mdi-phone</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.spaceTypes.phoneBooth.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.spaceTypes.phoneBooth.description') }}</p>
                                <div class="space-specs">
                                    <div class="spec">
                                        <v-icon size="16">mdi-account</v-icon>
                                        <span>{{ $t('bookingAnalytics.spaceTypes.phoneBooth.capacity') }}</span>
                                    </div>
                                    <div class="spec">
                                        <v-icon size="16">mdi-volume-off</v-icon>
                                        <span>{{ $t('bookingAnalytics.spaceTypes.phoneBooth.equipment') }}</span>
                                    </div>
                                </div>
                                <div class="space-availability">
                                    <span class="available-now">{{ $t('bookingAnalytics.spaceTypes.phoneBooth.availability') }}</span>
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="space-type-card lounge">
                                <div class="space-icon">
                                    <v-icon size="36" color="white">mdi-sofa</v-icon>
                                </div>
                                <h4>{{ $t('bookingAnalytics.spaceTypes.lounge.title') }}</h4>
                                <p>{{ $t('bookingAnalytics.spaceTypes.lounge.description') }}</p>
                                <div class="space-specs">
                                    <div class="spec">
                                        <v-icon size="16">mdi-account-multiple</v-icon>
                                        <span>{{ $t('bookingAnalytics.spaceTypes.lounge.capacity') }}</span>
                                    </div>
                                    <div class="spec">
                                        <v-icon size="16">mdi-coffee</v-icon>
                                        <span>{{ $t('bookingAnalytics.spaceTypes.lounge.equipment') }}</span>
                                    </div>
                                </div>
                                <div class="space-availability">
                                    <span class="available-now">{{ $t('bookingAnalytics.spaceTypes.lounge.availability') }}</span>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Best Practices -->
                <div class="best-practices-section">
                    <div class="section-header">
                        <h3>{{ $t('bookingAnalytics.bestPractices.title') }}</h3>
                        <p>{{ $t('bookingAnalytics.bestPractices.subtitle') }}</p>
                    </div>

                    <div class="practices-grid">
                        <div class="practice-card">
                            <div class="practice-number">1</div>
                            <h4>{{ $t('bookingAnalytics.bestPractices.practice1.title') }}</h4>
                            <p>{{ $t('bookingAnalytics.bestPractices.practice1.description') }}</p>
                        </div>

                        <div class="practice-card">
                            <div class="practice-number">2</div>
                            <h4>{{ $t('bookingAnalytics.bestPractices.practice2.title') }}</h4>
                            <p>{{ $t('bookingAnalytics.bestPractices.practice2.description') }}</p>
                        </div>

                        <div class="practice-card">
                            <div class="practice-number">3</div>
                            <h4>{{ $t('bookingAnalytics.bestPractices.practice3.title') }}</h4>
                            <p>{{ $t('bookingAnalytics.bestPractices.practice3.description') }}</p>
                        </div>

                        <div class="practice-card">
                            <div class="practice-number">4</div>
                            <h4>{{ $t('bookingAnalytics.bestPractices.practice4.title') }}</h4>
                            <p>{{ $t('bookingAnalytics.bestPractices.practice4.description') }}</p>
                        </div>

                        <div class="practice-card">
                            <div class="practice-number">5</div>
                            <h4>{{ $t('bookingAnalytics.bestPractices.practice5.title') }}</h4>
                            <p>{{ $t('bookingAnalytics.bestPractices.practice5.description') }}</p>
                        </div>

                        <div class="practice-card">
                            <div class="practice-number">6</div>
                            <h4>{{ $t('bookingAnalytics.bestPractices.practice6.title') }}</h4>
                            <p>{{ $t('bookingAnalytics.bestPractices.practice6.description') }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Data
const visible = ref(false);
const totalBookings = ref(1247);
const occupancyRate = ref(78);
const availableSpaces = ref(156);
const peakHours = ref('10-3');

// Donut Chart
const donutSeries = ref([44, 25, 18, 13]);
const donutOptions = computed(() => ({
    chart: {
        type: 'donut',
        fontFamily: 'Inter, sans-serif'
    },
    colors: ['#1E5A8E', '#FF6B35', '#2B7BC4', '#FFA07A'],
    labels: [
        t('bookingAnalytics.charts.spaceDistribution.meetingRooms'),
        t('bookingAnalytics.charts.spaceDistribution.teamZones'),
        t('bookingAnalytics.charts.spaceDistribution.privateDesks'),
        t('bookingAnalytics.charts.spaceDistribution.otherSpaces')
    ],
    legend: {
        show: false
    },
    dataLabels: {
        enabled: true,
        formatter: (val) => `${Math.round(val)}%`
    },
    plotOptions: {
        pie: {
            donut: {
                size: '70%',
                labels: {
                    show: true,
                    total: {
                        show: true,
                        label: t('bookingAnalytics.chartLabels.total'),
                        formatter: () => '1247'
                    }
                }
            }
        }
    }
}));

// Bar Chart
const barSeries = ref([{
    name: t('bookingAnalytics.chartLabels.bookings'),
    data: [145, 178, 203, 189, 234, 156, 98]
}]);
const barOptions = computed(() => ({
    chart: {
        type: 'bar',
        fontFamily: 'Inter, sans-serif',
        toolbar: {
            show: false
        }
    },
    colors: ['#1E5A8E'],
    plotOptions: {
        bar: {
            borderRadius: 8,
            columnWidth: '60%'
        }
    },
    dataLabels: {
        enabled: false
    },
    xaxis: {
        categories: [
            t('bookingAnalytics.weekdays.mon'),
            t('bookingAnalytics.weekdays.tue'),
            t('bookingAnalytics.weekdays.wed'),
            t('bookingAnalytics.weekdays.thu'),
            t('bookingAnalytics.weekdays.fri'),
            t('bookingAnalytics.weekdays.sat'),
            t('bookingAnalytics.weekdays.sun')
        ]
    },
    yaxis: {
        title: {
            text: t('bookingAnalytics.chartLabels.numberOfBookings')
        }
    },
    grid: {
        borderColor: '#f3f4f6'
    }
}));

// Area Chart
const areaSeries = ref([{
    name: t('bookingAnalytics.chartLabels.bookings'),
    data: [12, 18, 28, 45, 67, 89, 112, 98, 87, 76, 54, 32, 18, 12, 8, 6, 4, 3, 2, 1, 1, 0, 0, 0]
}]);
const areaOptions = computed(() => ({
    chart: {
        type: 'area',
        fontFamily: 'Inter, sans-serif',
        toolbar: {
            show: false
        }
    },
    colors: ['#1E5A8E'],
    dataLabels: {
        enabled: false
    },
    stroke: {
        curve: 'smooth',
        width: 2
    },
    fill: {
        type: 'gradient',
        gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.6,
            opacityTo: 0.1
        }
    },
    xaxis: {
        categories: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23'],
        title: {
            text: t('bookingAnalytics.chartLabels.hourOfDay')
        }
    },
    yaxis: {
        title: {
            text: t('bookingAnalytics.chartLabels.bookingFrequency')
        }
    },
    grid: {
        borderColor: '#f3f4f6'
    }
}));

// Horizontal Bar Chart
const horizontalBarSeries = ref([{
    data: [234, 189, 178, 156, 145]
}]);
const horizontalBarOptions = computed(() => ({
    chart: {
        type: 'bar',
        fontFamily: 'Inter, sans-serif',
        toolbar: {
            show: false
        }
    },
    colors: ['#1E5A8E'],
    plotOptions: {
        bar: {
            borderRadius: 8,
            horizontal: true,
            barHeight: '70%'
        }
    },
    dataLabels: {
        enabled: true
    },
    xaxis: {
        categories: [
            t('bookingAnalytics.roomNames.conferenceA'),
            t('bookingAnalytics.roomNames.teamZone3'),
            t('bookingAnalytics.roomNames.meetingB'),
            t('bookingAnalytics.roomNames.focusRoom2'),
            t('bookingAnalytics.roomNames.deskArea5')
        ]
    },
    grid: {
        borderColor: '#f3f4f6'
    }
}));

// Methods
function open() {
    visible.value = true;
}

function closeDialog() {
    visible.value = false;
}

// Expose methods
defineExpose({ open, close: closeDialog });
</script>
<style scoped>
/* Card Base */
.booking-card {
    background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
    min-height: 100vh;
    position: relative;
}

/* Header */
.card-header {
    background: linear-gradient(135deg, #0A1929 0%, #1A2F42 100%);
    padding: 32px 40px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.left-section {
    flex: 1;
}

.header-badge {
    display: inline-block;
    padding: 6px 16px;
    background: rgba(30, 90, 142, 0.2);
    border: 1px solid rgba(30, 90, 142, 0.3);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    color: #60a5fa;
    margin-bottom: 16px;
}

.card-header h2 {
    font-size: 32px;
    font-weight: 800;
    color: #ffffff;
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;
}

.header-description {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    margin: 0;
}

.right-section {
    margin-left: 24px;
}

.dialog-close-btn {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.dialog-close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
}

.dialog-close-btn .v-icon {
    color: white;
}

/* Main Container */
.booking-container {
    padding: 40px;
    max-width: 1600px;
    margin: 0 auto;
}

/* Stats Overview */
.stats-overview {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 24px;
    margin-bottom: 48px;
}

.stat-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    display: flex;
    align-items: center;
    gap: 20px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 1px solid rgba(0, 0, 0, 0.04);
}

.stat-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 32px rgba(30, 90, 142, 0.15);
}

.stat-icon {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.stat-icon.active {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    box-shadow: 0 8px 24px rgba(30, 90, 142, 0.25);
}

.stat-icon.occupied {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.25);
}

.stat-icon.available {
    background: linear-gradient(135deg, #FF6B35 0%, #FFA07A 100%);
    box-shadow: 0 8px 24px rgba(255, 107, 53, 0.25);
}

.stat-icon.peak {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
    box-shadow: 0 8px 24px rgba(139, 92, 246, 0.25);
}

.stat-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.stat-value {
    font-size: 32px;
    font-weight: 800;
    color: #1f2937;
    line-height: 1;
}

.stat-label {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.stat-trend {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    margin-top: 4px;
}

.stat-trend.positive {
    color: #10b981;
}

.stat-trend.neutral {
    color: #6b7280;
}

/* Section Styling */
.features-section,
.charts-section,
.space-types-section,
.best-practices-section {
    margin-bottom: 48px;
}

.section-header {
    text-align: center;
    margin-bottom: 40px;
}

.section-header h3 {
    font-size: 32px;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 12px 0;
    letter-spacing: -0.5px;
}

.section-header p {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
}

/* Feature Cards */
.feature-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    text-align: center;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid transparent;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    height: 100%;
}

.feature-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.feature-card.instant:hover {
    border-color: #fbbf24;
}

.feature-card.calendar:hover {
    border-color: #1E5A8E;
}

.feature-card.recurring:hover {
    border-color: #10b981;
}

.feature-card.notification:hover {
    border-color: #FF6B35;
}

.feature-card.analytics:hover {
    border-color: #8b5cf6;
}

.feature-card.mobile:hover {
    border-color: #ec4899;
}

.feature-card.checkin:hover {
    border-color: #06b6d4;
}

.feature-card.automation:hover {
    border-color: #f59e0b;
}

.feature-icon {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    transition: transform 0.4s ease;
}

.feature-card:hover .feature-icon {
    transform: scale(1.1) rotate(5deg);
}

.feature-card.instant .feature-icon {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.feature-card.calendar .feature-icon {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.feature-card.recurring .feature-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.feature-card.notification .feature-icon {
    background: linear-gradient(135deg, #FF6B35 0%, #FFA07A 100%);
}

.feature-card.analytics .feature-icon {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.feature-card.mobile .feature-icon {
    background: linear-gradient(135deg, #ec4899 0%, #f472b6 100%);
}

.feature-card.checkin .feature-icon {
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
}

.feature-card.automation .feature-icon {
    background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
}

.feature-card h4 {
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px 0;
}

.feature-card p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.6;
    margin: 0;
}

/* Chart Containers */
.chart-container {
    background: white;
    border-radius: 20px;
    padding: 32px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
    height: 100%;
}

.chart-header {
    margin-bottom: 24px;
}

.chart-header h4 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 8px 0;
}

.chart-header p {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
}

.chart-wrapper {
    margin: 20px 0;
}

.chart-legend {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 24px;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    font-weight: 500;
    color: #4b5563;
}

.legend-color {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    flex-shrink: 0;
}

.legend-value {
    margin-left: auto;
    font-weight: 700;
    color: #1f2937;
}

.chart-insights {
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.insight {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: #f9fafb;
    border-radius: 8px;
    font-size: 13px;
    color: #4b5563;
}

/* Space Type Cards */
.space-type-card {
    background: white;
    border-radius: 20px;
    padding: 32px;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid transparent;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    height: 100%;
    position: relative;
}

.space-type-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.space-type-card.meeting {
    border-color: rgba(30, 90, 142, 0.1);
}

.space-type-card.meeting:hover {
    border-color: #1E5A8E;
}

.space-type-card.desk {
    border-color: rgba(16, 185, 129, 0.1);
}

.space-type-card.desk:hover {
    border-color: #10b981;
}

.space-type-card.team {
    border-color: rgba(255, 107, 53, 0.1);
}

.space-type-card.team:hover {
    border-color: #FF6B35;
}

.space-type-card.focus {
    border-color: rgba(139, 92, 246, 0.1);
}

.space-type-card.focus:hover {
    border-color: #8b5cf6;
}

.space-type-card.phone {
    border-color: rgba(236, 72, 153, 0.1);
}

.space-type-card.phone:hover {
    border-color: #ec4899;
}

.space-type-card.lounge {
    border-color: rgba(6, 182, 212, 0.1);
}

.space-type-card.lounge:hover {
    border-color: #06b6d4;
}

.space-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    padding: 4px 12px;
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    color: white;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.5px;
    border-radius: 12px;
}

.space-icon {
    width: 80px;
    height: 80px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    transition: transform 0.4s ease;
}

.space-type-card:hover .space-icon {
    transform: scale(1.1) rotate(-5deg);
}

.space-type-card.meeting .space-icon {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.space-type-card.desk .space-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.space-type-card.team .space-icon {
    background: linear-gradient(135deg, #FF6B35 0%, #FFA07A 100%);
}

.space-type-card.focus .space-icon {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.space-type-card.phone .space-icon {
    background: linear-gradient(135deg, #ec4899 0%, #f472b6 100%);
}

.space-type-card.lounge .space-icon {
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
}

.space-type-card h4 {
    font-size: 22px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px 0;
}

.space-type-card p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.6;
    margin: 0 0 20px 0;
}

.space-specs {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
}

.spec {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.spec .v-icon {
    color: #9ca3af;
}

.space-availability {
    padding-top: 20px;
    border-top: 2px solid #f3f4f6;
}

.available-now {
    display: inline-block;
    padding: 8px 16px;
    background: #ecfdf5;
    color: #059669;
    font-size: 13px;
    font-weight: 700;
    border-radius: 20px;
}

/* Best Practices */
.practices-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 24px;
}

.practice-card {
    background: white;
    border-radius: 16px;
    padding: 28px;
    border-left: 4px solid #1E5A8E;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease;
}

.practice-card:hover {
    transform: translateX(8px);
    box-shadow: 0 8px 24px rgba(30, 90, 142, 0.15);
}

.practice-number {
    display: inline-block;
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    color: white;
    font-size: 16px;
    font-weight: 700;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
}

.practice-card h4 {
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px 0;
}

.practice-card p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.6;
    margin: 0;
}

/* Responsive */
@media (max-width: 1024px) {
    .booking-container {
        padding: 32px 24px;
    }

    .stats-overview {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .card-header {
        padding: 24px;
        flex-direction: column;
        gap: 20px;
    }

    .card-header h2 {
        font-size: 24px;
    }

    .header-description {
        font-size: 14px;
    }

    .right-section {
        margin-left: 0;
        align-self: flex-end;
    }

    .booking-container {
        padding: 24px 16px;
    }

    .stats-overview {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .stat-card {
        padding: 20px;
    }

    .stat-icon {
        width: 60px;
        height: 60px;
    }

    .stat-value {
        font-size: 28px;
    }

    .section-header h3 {
        font-size: 24px;
    }

    .practices-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 480px) {
    .card-header h2 {
        font-size: 20px;
    }

    .booking-container {
        padding: 20px 12px;
    }

    .chart-container {
        padding: 20px;
    }

    .space-type-card {
        padding: 24px;
    }
}

/* Scrollbar */
.booking-container::-webkit-scrollbar {
    width: 8px;
}

.booking-container::-webkit-scrollbar-track {
    background: #f3f4f6;
}

.booking-container::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
}

.booking-container::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
}
</style>